import React, { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/Card'
import { Badge } from 'components/ui/Badge'
import { Button } from 'components/ui/Button'
import { Dropdown, DropdownItem } from 'components/ui/Dropdown'
import PDFViewer from 'components/ui/PDFViewer'
import { useTranslation } from 'react-i18next'
import { candidatesApi, uploadsApi, applicationsApi, type Candidate, type Upload } from 'lib/api'
import { EditCandidateModal } from 'features/candidates/components/EditCandidateModal'
import { SendEmailModal } from 'features/candidates/components/SendEmailModal'
import { CandidateReceivedEmails } from 'features/candidates/components/CandidateReceivedEmails'
import { ScheduleMeetingModal } from 'features/candidates/components/ScheduleMeetingModal'
import { getStatusColor, formatDate } from 'lib/utils'
import { PageHeader } from 'components/layout/PageHeader'
import { translateFrToEn } from 'lib/translationService'
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Edit,
  Upload as UploadIcon,
  Download,
  FileText,
  Users,
  Eye,
  File,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  Briefcase,
  FolderOpen,
  Award,
  Building2,
  GraduationCap,
  Cake,
  Globe,
  Zap,
  Clock,
  AlertCircle,
  CheckCircle2,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts'

export function CandidateDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const [candidate, setCandidate] = useState<Candidate | null>(null)
  const [uploads, setUploads] = useState<Upload[]>([])
  const [loading, setLoading] = useState(true)
  const [viewingPDF, setViewingPDF] = useState<{ url: string; fileName: string } | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [adminComment, setAdminComment] = useState('')
  const [isEditingComment, setIsEditingComment] = useState(false)
  const [savingComment, setSavingComment] = useState(false)
  const [showSendEmailModal, setShowSendEmailModal] = useState(false)
  const [emailRefreshKey, setEmailRefreshKey] = useState(0)
  const [scheduleMeetingType, setScheduleMeetingType] = useState<'call' | 'interview' | null>(null)
  const [candidateApplications, setCandidateApplications] = useState<Array<{
    id: number,
    offer: number,
    offer_id: number,
    offer_title: string,
    status: string,
    match_score: number,
    current_step_name?: string | null,
    current_step_id?: number | null,
    current_step_status?: string | null,
    process_status?: string | null,
    applied_date: string
  }>>([])
  const [loadingApplications, setLoadingApplications] = useState(false)
  const [applicationsPage, setApplicationsPage] = useState(1)
  const [applicationsTotalCount, setApplicationsTotalCount] = useState(0)
  const applicationsPageSize = 20
  const applicationsTotalPages = Math.max(1, Math.ceil(applicationsTotalCount / applicationsPageSize))
  const [projectsExpanded, setProjectsExpanded] = useState(false)
  const [educationExpanded, setEducationExpanded] = useState(false)
  const [experiencesExpanded, setExperiencesExpanded] = useState(false)
  const [mainMissionsExpanded, setMainMissionsExpanded] = useState(false)
  const [translatedRole, setTranslatedRole] = useState<string>('')
  const [translatedBio, setTranslatedBio] = useState<string>('')
  const [translatedHardSkills, setTranslatedHardSkills] = useState<string[]>([])
  const [translatedSoftSkills, setTranslatedSoftSkills] = useState<string[]>([])
  const [translatedNiceToHave, setTranslatedNiceToHave] = useState<string[]>([])
  const [translatedLanguages, setTranslatedLanguages] = useState<Array<{name: string; level?: string}>>([])
  const [translatedProjects, setTranslatedProjects] = useState<any[]>([])
  const [translatedMainMissions, setTranslatedMainMissions] = useState<string[]>([])
  const [translatedEducations, setTranslatedEducations] = useState<string[]>([])
  const [translatedCertifications, setTranslatedCertifications] = useState<string[]>([])
  const [translatedExperiences, setTranslatedExperiences] = useState<any[]>([])
  
  // Translation stage tracking for sequential translation
  const [translationStage, setTranslationStage] = useState<'idle' | 'role_bio' | 'hard_skills' | 'soft_skills' | 'nice_to_have' | 'languages' | 'projects' | 'main_missions' | 'educations' | 'certifications' | 'experiences'>('idle')
  
  // Duplicate candidates handling
  const [allProfilesWithEmail, setAllProfilesWithEmail] = useState<Candidate[]>([])
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null)

  const fetchedRef = useRef<string | null>(null)
  
  useEffect(() => {
    if (!id || fetchedRef.current === id) return
    fetchedRef.current = id

    const fetchData = async () => {
      if (!id) return

      try {
        // Fetch candidate first to get email for duplicate search
        const candidateResponse = await candidatesApi.getById(parseInt(id))
        const current = candidateResponse.data
        
        let allCandidatesResponse: Candidate[] = []
        
        // Fetch uploads and duplicates concurrently if email exists
        if (current.email) {
          const normalized = current.email.toLowerCase().trim()
          const [uploadsResponse, duplicateSearchResponse] = await Promise.all([
            uploadsApi.getByCandidateId(parseInt(id)),
            candidatesApi.getAll({ search: normalized, page_size: 50 })
          ])
          setUploads(uploadsResponse.data)
          allCandidatesResponse = duplicateSearchResponse.data.results || []
        } else {
          const uploadsResponse = await uploadsApi.getByCandidateId(parseInt(id))
          setUploads(uploadsResponse.data)
        }

        setCandidate(current)
        setAdminComment(current.admin_comment || '')
        
        // Find all candidates with same email (including current)
        if (current.email) {
          const normalized = current.email.toLowerCase().trim()
          const allProfiles = [current, ...(allCandidatesResponse || [])
            .filter(c => c.id !== current.id && c.email && c.email.toLowerCase().trim() === normalized)]
            .sort((a, b) => {
              const ta = a.created_at ? new Date(a.created_at).getTime() : 0
              const tb = b.created_at ? new Date(b.created_at).getTime() : 0
              return tb - ta
            })
          setAllProfilesWithEmail(allProfiles)
          // Set the current profile from URL as selected (not the most recent)
          setSelectedProfileId(current.id)
        } else {
          setAllProfilesWithEmail([current])
          setSelectedProfileId(current.id)
        }
      } catch (error) {
        console.error('Error fetching candidate data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [id])

  useEffect(() => {
    setApplicationsPage(1)
  }, [id])

  useEffect(() => {
    const fetchApplications = async () => {
      if (!candidate || !id) return

      setLoadingApplications(true)
      try {
        const response = await applicationsApi.getByCandidate(parseInt(id), {
          page: applicationsPage,
          page_size: applicationsPageSize,
        })
        setCandidateApplications(response.data.applications || [])
        setApplicationsTotalCount(response.data.total_count || 0)
      } catch (error) {
        console.error('Error fetching candidate applications:', error)
        setCandidateApplications([])
        setApplicationsTotalCount(0)
      } finally {
        setLoadingApplications(false)
      }
    }

    if (candidate) {
      fetchApplications()
    }
  }, [candidate, id, applicationsPage])

  // Load saved translations from database if they exist
  useEffect(() => {
    if (!candidate || i18n.language !== 'en') {
      return
    }

    const translation_en = (candidate as any).translation_en
    if (translation_en && typeof translation_en === 'object' && Object.keys(translation_en).length > 0) {
      // Load translations from database
      console.log('Loading saved translations from database')
      setTranslatedRole(translation_en.role || '')
      setTranslatedBio(translation_en.bio || '')
      setTranslatedHardSkills(translation_en.hard_skills || [])
      setTranslatedSoftSkills(translation_en.soft_skills || [])
      setTranslatedNiceToHave(translation_en.nice_to_have || [])
      setTranslatedLanguages(translation_en.languages || [])
      setTranslatedProjects(translation_en.projects || [])
      setTranslatedMainMissions(translation_en.main_missions || [])
      setTranslatedEducations(translation_en.educations || [])
      setTranslatedCertifications(translation_en.certifications || [])
      setTranslatedExperiences(translation_en.experiences || [])
      // Skip translation process, go directly to idle
      setTranslationStage('idle')
    } else {
      // No saved translations, start the translation process
      setTranslationStage('role_bio')
    }
  }, [candidate?.id, i18n.language])

  // Save all translations to database when translation is complete
  useEffect(() => {
    if (translationStage !== 'idle' || i18n.language !== 'en') {
      return
    }

    if (
      !candidate ||
      (!translatedRole && !translatedBio && translatedHardSkills.length === 0 && translatedSoftSkills.length === 0 && 
       translatedNiceToHave.length === 0 && translatedLanguages.length === 0 && translatedProjects.length === 0 &&
       translatedMainMissions.length === 0 && translatedEducations.length === 0 &&
       translatedCertifications.length === 0 && translatedExperiences.length === 0)
    ) {
      return
    }

    const saveTranslations = async () => {
      try {
        const translationData = {
          translation_en: {
            role: translatedRole,
            bio: translatedBio,
            hard_skills: translatedHardSkills,
            soft_skills: translatedSoftSkills,
            nice_to_have: translatedNiceToHave,
            languages: translatedLanguages,
            projects: translatedProjects,
            main_missions: translatedMainMissions,
            educations: translatedEducations,
            certifications: translatedCertifications,
            experiences: translatedExperiences,
            timestamp: new Date().toISOString()
          }
        }

        await candidatesApi.update(candidate.id, translationData)
        console.log('Translations saved to database successfully')
      } catch (error) {
        console.error('Error saving translations to database:', error)
      }
    }

    // Debounce save to avoid too frequent updates
    const timer = setTimeout(saveTranslations, 1000)
    return () => clearTimeout(timer)
  }, [translationStage, translatedRole, translatedBio, translatedHardSkills, translatedSoftSkills, 
      translatedNiceToHave, translatedLanguages, translatedProjects, translatedMainMissions, 
      translatedEducations, translatedCertifications, translatedExperiences, 
      candidate, i18n.language])

  // Sequential translation: Start with role and bio
  useEffect(() => {
    if (!candidate || i18n.language !== 'en' || translationStage !== 'role_bio') {
      if (!candidate || i18n.language !== 'en') {
        setTranslationStage('idle')
        setTranslatedRole(candidate?.role || '')
        setTranslatedBio(candidate?.bio || '')
        setTranslatedHardSkills([])
        setTranslatedSoftSkills([])
        setTranslatedLanguages([])
        setTranslatedProjects([])
        setTranslatedMainMissions([])
        setTranslatedEducations([])
        setTranslatedCertifications([])
        setTranslatedExperiences([])
      }
      return
    }

    const translateRoleAndBio = async () => {
      try {
        if (candidate.role) {
          setTranslatedRole(await translateFrToEn(candidate.role))
        } else {
          setTranslatedRole('')
        }

        if (candidate.bio) {
          setTranslatedBio(await translateFrToEn(candidate.bio))
        } else {
          setTranslatedBio('')
        }

        // After role and bio are done, move to hard_skills
        setTranslationStage('hard_skills')
      } catch (error) {
        console.error('Error translating role/bio:', error)
        setTranslatedRole(candidate.role || '')
        setTranslatedBio(candidate.bio || '')
        setTranslationStage('hard_skills')
      }
    }

    translateRoleAndBio()
  }, [candidate, translationStage, i18n.language, selectedProfileId])

  // Step 2: Translate hard_skills after role and bio are done
  useEffect(() => {
    if (!candidate || i18n.language !== 'en' || translationStage !== 'hard_skills') {
      return
    }

    const hardSkills = (candidate as any).skills || []
    if (!Array.isArray(hardSkills) || hardSkills.length === 0) {
      setTranslatedHardSkills([])
      setTranslationStage('soft_skills')
      return
    }

    const translateHardSkillsProgressively = async () => {
      const translated: string[] = []
      for (const skill of hardSkills) {
        try {
          const translatedSkill = await translateFrToEn(skill)
          translated.push(translatedSkill)
          setTranslatedHardSkills([...translated])
        } catch (error) {
          console.error('Error translating hard skill:', error)
          translated.push(skill)
          setTranslatedHardSkills([...translated])
        }
      }
      setTranslationStage('soft_skills')
    }

    translateHardSkillsProgressively()
  }, [candidate, translationStage, i18n.language, selectedProfileId])

  // Step 3: Translate soft_skills after hard_skills are done
  useEffect(() => {
    if (!candidate || i18n.language !== 'en' || translationStage !== 'soft_skills') {
      return
    }

    const softSkills = (candidate as any).soft_skills || []
    if (!Array.isArray(softSkills) || softSkills.length === 0) {
      setTranslatedSoftSkills([])
      setTranslationStage('nice_to_have')
      return
    }

    const translateSoftSkillsProgressively = async () => {
      const translated: string[] = []
      for (const skill of softSkills) {
        try {
          const translatedSkill = await translateFrToEn(skill)
          translated.push(translatedSkill)
          setTranslatedSoftSkills([...translated])
        } catch (error) {
          console.error('Error translating soft skill:', error)
          translated.push(skill)
          setTranslatedSoftSkills([...translated])
        }
      }
      setTranslationStage('nice_to_have')
    }

    translateSoftSkillsProgressively()
  }, [candidate, translationStage, i18n.language, selectedProfileId])

  // Step 4: Translate nice_to_have after soft_skills are done
  useEffect(() => {
    if (!candidate || i18n.language !== 'en' || translationStage !== 'nice_to_have') {
      return
    }

    const niceToHave = (candidate as any).nice_to_have || []
    if (!Array.isArray(niceToHave) || niceToHave.length === 0) {
      setTranslatedNiceToHave([])
      setTranslationStage('languages')
      return
    }

    const translateNiceToHaveProgressively = async () => {
      const translated: string[] = []
      for (const item of niceToHave) {
        try {
          const translatedItem = await translateFrToEn(item)
          translated.push(translatedItem)
          setTranslatedNiceToHave([...translated])
        } catch (error) {
          console.error('Error translating nice_to_have item:', error)
          translated.push(item)
          setTranslatedNiceToHave([...translated])
        }
      }
      setTranslationStage('languages')
    }

    translateNiceToHaveProgressively()
  }, [candidate, translationStage, i18n.language, selectedProfileId])

  // Step 4: Translate languages after nice_to_have are done
  useEffect(() => {
    if (!candidate || i18n.language !== 'en' || translationStage !== 'languages') {
      return
    }

    const languages = (candidate as any).languages || []
    if (!Array.isArray(languages) || languages.length === 0) {
      setTranslatedLanguages([])
      setTranslationStage('projects')
      return
    }

    const translateLanguagesProgressively = async () => {
      const translated: Array<{name: string; level?: string}> = []
      for (const lang of languages) {
        try {
          const name = typeof lang === 'string' ? lang : (lang.name || '')
          const level = typeof lang === 'object' ? lang.level : ''
          const translatedName = name ? await translateFrToEn(name) : ''
          translated.push({ name: translatedName, level })
          setTranslatedLanguages([...translated])
        } catch (error) {
          console.error('Error translating language:', error)
          const name = typeof lang === 'string' ? lang : (lang.name || '')
          const level = typeof lang === 'object' ? lang.level : ''
          translated.push({ name, level })
          setTranslatedLanguages([...translated])
        }
      }
      setTranslationStage('projects')
    }

    translateLanguagesProgressively()
  }, [candidate, translationStage, i18n.language, selectedProfileId])

  // Step 4: Translate projects after languages are done
  useEffect(() => {
    if (i18n.language !== 'en' || translationStage !== 'projects') {
      return
    }

    const { projets: currentProjets } = getExtractedData()
    if (!Array.isArray(currentProjets) || currentProjets.length === 0) {
      setTranslatedProjects([])
      setTranslationStage('main_missions')
      return
    }

    const translateProjectsProgressively = async () => {
      const translated: any[] = []
      for (const projet of currentProjets) {
        try {
          let translatedProjet: any
          if (typeof projet === 'string') {
            translatedProjet = await translateFrToEn(projet)
          } else if (typeof projet === 'object') {
            translatedProjet = { ...projet }
            if (translatedProjet.nom) {
              translatedProjet.nom = await translateFrToEn(translatedProjet.nom)
            }
            if (translatedProjet.name) {
              translatedProjet.name = await translateFrToEn(translatedProjet.name)
            }
            if (translatedProjet.description) {
              translatedProjet.description = await translateFrToEn(translatedProjet.description)
            }
            if (translatedProjet.technologies && Array.isArray(translatedProjet.technologies)) {
              const translatedTechs: string[] = []
              for (const tech of translatedProjet.technologies) {
                translatedTechs.push(await translateFrToEn(tech))
              }
              translatedProjet.technologies = translatedTechs
            }
          } else {
            translatedProjet = projet
          }
          translated.push(translatedProjet)
          setTranslatedProjects([...translated])
        } catch (error) {
          console.error('Error translating project:', error)
          translated.push(projet)
          setTranslatedProjects([...translated])
        }
      }
      setTranslationStage('main_missions')
    }

    translateProjectsProgressively()
  }, [candidate, uploads, translationStage, i18n.language])

  // Step 5: Translate main missions after projects are done
  useEffect(() => {
    if (!candidate || i18n.language !== 'en' || translationStage !== 'main_missions') {
      return
    }

    const mainMissions = (candidate as any).main_missions
    if (!mainMissions) {
      setTranslatedMainMissions([])
      setTranslationStage('educations')
      return
    }

    const missionsArray = Array.isArray(mainMissions) ? mainMissions : [mainMissions]
    const filteredMissions = missionsArray.filter((m: any) => m && m.toString().trim() !== '')

    if (filteredMissions.length === 0) {
      setTranslatedMainMissions([])
      setTranslationStage('educations')
      return
    }

    const translateMissionsProgressively = async () => {
      const translated: string[] = []
      for (const mission of filteredMissions) {
        try {
          const translatedMission = await translateFrToEn(mission.toString())
          translated.push(translatedMission)
          setTranslatedMainMissions([...translated])
        } catch (error) {
          console.error('Error translating mission:', error)
          translated.push(mission.toString())
          setTranslatedMainMissions([...translated])
        }
      }
      setTranslationStage('educations')
    }

    translateMissionsProgressively()
  }, [candidate, translationStage, i18n.language, selectedProfileId])

  // Step 6: Translate educations after main missions are done
  useEffect(() => {
    if (i18n.language !== 'en' || translationStage !== 'educations') {
      return
    }

    const { education: currentEdu } = getExtractedData()

    if (!Array.isArray(currentEdu) || currentEdu.length === 0) {
      setTranslatedEducations([])
      setTranslationStage('certifications')
      return
    }

    const translateEducationsProgressively = async () => {
      const translatedEdu: string[] = []
      for (const edu of currentEdu) {
        try {
          const translatedEducation = await translateFrToEn(edu)
          translatedEdu.push(translatedEducation)
          setTranslatedEducations([...translatedEdu])
        } catch (error) {
          console.error('Error translating education:', error)
          translatedEdu.push(edu)
          setTranslatedEducations([...translatedEdu])
        }
      }
      setTranslationStage('certifications')
    }

    translateEducationsProgressively()
  }, [candidate, uploads, translationStage, i18n.language, selectedProfileId])

  // Step 7: Translate certifications after educations are done
  useEffect(() => {
    if (i18n.language !== 'en' || translationStage !== 'certifications') {
      return
    }

    const { certifications: currentCerts } = getExtractedData()

    if (!Array.isArray(currentCerts) || currentCerts.length === 0) {
      setTranslatedCertifications([])
      setTranslationStage('experiences')
      return
    }

    const translateCertificationsProgressively = async () => {
      const translatedCerts: string[] = []
      for (const cert of currentCerts) {
        try {
          const translatedCert = await translateFrToEn(cert)
          translatedCerts.push(translatedCert)
          setTranslatedCertifications([...translatedCerts])
        } catch (error) {
          console.error('Error translating certification:', error)
          translatedCerts.push(cert)
          setTranslatedCertifications([...translatedCerts])
        }
      }
      setTranslationStage('experiences')
    }

    translateCertificationsProgressively()
  }, [candidate, uploads, translationStage, i18n.language, selectedProfileId])

  // Step 8: Translate experiences after certifications are done
  useEffect(() => {
    if (i18n.language !== 'en' || translationStage !== 'experiences') {
      return
    }

    const { experiences: currentExps } = getExtractedData()

    if (!Array.isArray(currentExps) || currentExps.length === 0) {
      setTranslatedExperiences([])
      // No experiences to translate, mark as complete
      setTranslationStage('idle')
      return
    }

    const translateExperiencesProgressively = async () => {
      const translatedExps: any[] = []
      for (const exp of currentExps) {
        try {
          let translatedExp: any
          if (typeof exp === 'string') {
            translatedExp = await translateFrToEn(exp)
          } else if (typeof exp === 'object') {
            translatedExp = { ...exp }
            if (translatedExp.poste) {
              translatedExp.poste = await translateFrToEn(translatedExp.poste)
            }
            if (translatedExp.position) {
              translatedExp.position = await translateFrToEn(translatedExp.position)
            }
            if (translatedExp.entreprise) {
              translatedExp.entreprise = await translateFrToEn(translatedExp.entreprise)
            }
            if (translatedExp.company) {
              translatedExp.company = await translateFrToEn(translatedExp.company)
            }
            if (translatedExp.missions && Array.isArray(translatedExp.missions)) {
              const translatedMissions: string[] = []
              for (const mission of translatedExp.missions) {
                translatedMissions.push(await translateFrToEn(mission))
              }
              translatedExp.missions = translatedMissions
            }
          } else {
            translatedExp = exp
          }
          translatedExps.push(translatedExp)
          setTranslatedExperiences([...translatedExps])
        } catch (error) {
          console.error('Error translating experience:', error)
          translatedExps.push(exp)
          setTranslatedExperiences([...translatedExps])
        }
      }
      // All translations are complete, mark as idle so the save effect triggers
      setTranslationStage('idle')
    }

    translateExperiencesProgressively()
  }, [candidate, uploads, translationStage, i18n.language, selectedProfileId])


  const handleSaveComment = async () => {
    if (!candidate) return

    setSavingComment(true)
    try {
      await candidatesApi.update(candidate.id, { admin_comment: adminComment })
      setCandidate({ ...candidate, admin_comment: adminComment })
      setIsEditingComment(false)
    } catch (error) {
      console.error('Error updating admin comment:', error)
    } finally {
      setSavingComment(false)
    }
  }

  const handleCancelEdit = () => {
    setAdminComment(candidate?.admin_comment || '')
    setIsEditingComment(false)
  }

  const handleViewPDF = async (upload: Upload) => {
    if (upload.file_type && upload.file_type.toLowerCase() === 'pdf') {
      try {
        console.log('🔍 DEBUG: Fetching PDF for react-pdf with Authorization header')

        // Fetch the PDF with Authorization header for react-pdf
        const response = await fetch(uploadsApi.view(upload.id), {
          credentials: 'include',
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        // Verify the response is actually a PDF
        const contentType = response.headers.get('content-type')
        console.log('🔍 DEBUG: Response content-type:', contentType)

        if (!contentType || !contentType.includes('pdf')) {
          throw new Error(`Invalid content type: ${contentType}`)
        }

        // Create blob URL for react-pdf
        const blob = await response.blob()
        console.log('🔍 DEBUG: Blob size:', blob.size, 'bytes')
        console.log('🔍 DEBUG: Blob type:', blob.type)

        if (blob.size === 0) {
          throw new Error('Received empty PDF file')
        }

        const blobUrl = URL.createObjectURL(blob)
        console.log('🔍 DEBUG: Created blob URL for react-pdf:', blobUrl)

        setViewingPDF({ url: blobUrl, fileName: upload.original_filename })
      } catch (error) {
        console.error('🔍 DEBUG: Error fetching PDF for react-pdf:', error)
        // Fallback to direct URL (won't work with react-pdf but shows error)
        const viewUrl = uploadsApi.view(upload.id)
        setViewingPDF({ url: viewUrl, fileName: upload.original_filename })
      }
    }
  }

  const handleDownloadFile = async (upload: Upload) => {
    try {
      const downloadUrl = uploadsApi.download(upload.id)

      const response = await fetch(downloadUrl, {
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      // Create blob and download
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = blobUrl
      link.download = upload.original_filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up blob URL
      URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Error downloading file:', error)
    }
  }

  const handleClosePDF = () => {
    // Clean up blob URL to prevent memory leaks
    if (viewingPDF?.url && viewingPDF.url.startsWith('blob:')) {
      console.log('🔍 DEBUG: Cleaning up blob URL:', viewingPDF.url)
      URL.revokeObjectURL(viewingPDF.url)
    }
    setViewingPDF(null)
  }

  // Helper function to translate experience message
  const translateExperienceMessage = (experienceStr: string): string => {
    if (!experienceStr) return t('candidateDetail.noExperience')
    
    // Check if it's already translated (French)
    if (experienceStr.includes('d\'expérience')) {
      // It's in French, convert to English first
      let enStr = experienceStr
      enStr = enStr.replace(/d'expérience$/, 'of experience')
      enStr = enStr.replace(/ans?/g, (match) => match === 'an' ? 'year' : 'years')
      enStr = enStr.replace(/mois/g, 'months')
      enStr = enStr.replace(/et/g, 'and')
      experienceStr = enStr
    }
    
    if (i18n.language === 'en') {
      return experienceStr // Already in English from backend
    }
    
    // Translate from English to French
    if (experienceStr === '0 months of experience') {
      return t('candidateDetail.noExperience')
    }
    
    let result = experienceStr
    
    // Parse the experience string
    const yearMatch = result.match(/(\d+)\s+years?/)
    const monthMatch = result.match(/(\d+)\s+months?/)
    
    let parts: string[] = []
    
    if (yearMatch) {
      const years = parseInt(yearMatch[1])
      const yearKey = years > 1 ? t('candidateDetail.experienceYears') : t('candidateDetail.experienceYear')
      parts.push(`${years} ${yearKey}`)
    }
    
    if (monthMatch) {
      const months = parseInt(monthMatch[1])
      const monthKey = months > 1 ? t('candidateDetail.experienceMonths') : t('candidateDetail.experienceMonths')
      if (parts.length > 0) {
        parts.push(t('candidateDetail.experienceAnd'))
      }
      parts.push(`${months} ${monthKey}`)
    }
    
    if (parts.length === 0) {
      return experienceStr
    }
    
    return parts.join(' ') + ' ' + t('candidateDetail.experienceOf')
  }

  // Helper function to extract data from candidate or CV uploads
  const getExtractedData = () => {
    const sourceCandidate = displayedCandidate || candidate
    if (!sourceCandidate) return { projets: [], certifications: [], experiences: [], education: [] }

    // Get latest processed CV upload
    const latestProcessedUpload = uploads
      .filter(u => u.status === 'processed' && u.processed_data)
      .sort((a, b) => {
        const dateA = a.processed_at ? new Date(a.processed_at).getTime() : 0
        const dateB = b.processed_at ? new Date(b.processed_at).getTime() : 0
        return dateB - dateA
      })[0]

    // Extract from structured_profile if available
    const structuredProfile = sourceCandidate.structured_profile || latestProcessedUpload?.processed_data?.structured_profile

    const formatEducationEntry = (entry: any) => {
      if (!entry) return ''
      if (typeof entry === 'string') return entry.trim()
      if (typeof entry === 'object') {
        const parts = [
          entry.diplome || entry.degree || entry.title || entry.name,
          entry.etablissement || entry.school || entry.university,
          entry.annee || entry.year || entry.date
        ].filter(Boolean)
        if (parts.length > 0) {
          return parts.join(' - ')
        }
      }
      return ''
    }

    const extractEducationList = (value: any): string[] => {
      if (!value) return []
      if (Array.isArray(value)) {
        return value
          .map(item => formatEducationEntry(item))
          .filter((item): item is string => Boolean(item && item.trim()))
      }
      if (typeof value === 'string') {
        return [value.trim()].filter(Boolean)
      }
      return []
    }

    // Extract projets
    let projets: Array<{ nom?: string; description?: string; technologies?: string[] } | string> = []
    if (sourceCandidate.projets && Array.isArray(sourceCandidate.projets) && sourceCandidate.projets.length > 0) {
      projets = sourceCandidate.projets
    } else if (structuredProfile?.projets && Array.isArray(structuredProfile.projets)) {
      projets = structuredProfile.projets
    } else if (latestProcessedUpload?.processed_data?.projets && Array.isArray(latestProcessedUpload.processed_data.projets)) {
      projets = latestProcessedUpload.processed_data.projets
    } else if (latestProcessedUpload?.processed_data?.structured_profile?.projets && Array.isArray(latestProcessedUpload.processed_data.structured_profile.projets)) {
      projets = latestProcessedUpload.processed_data.structured_profile.projets
    }

    // Extract certifications
    let certifications: string[] = []
    if (sourceCandidate.certifications && Array.isArray(sourceCandidate.certifications) && sourceCandidate.certifications.length > 0) {
      certifications = sourceCandidate.certifications
    } else if (structuredProfile?.certifications && Array.isArray(structuredProfile.certifications)) {
      certifications = structuredProfile.certifications.map((c: any) =>
        typeof c === 'string' ? c : (c.nom || c.name || '')
      ).filter((c: string) => c)
    } else if (latestProcessedUpload?.processed_data?.certifications && Array.isArray(latestProcessedUpload.processed_data.certifications)) {
      certifications = latestProcessedUpload.processed_data.certifications.map((c: any) =>
        typeof c === 'string' ? c : (c.nom || c.name || '')
      ).filter((c: string) => c)
    } else if (latestProcessedUpload?.processed_data?.structured_profile?.certifications && Array.isArray(latestProcessedUpload.processed_data.structured_profile.certifications)) {
      certifications = latestProcessedUpload.processed_data.structured_profile.certifications.map((c: any) =>
        typeof c === 'string' ? c : (c.nom || c.name || '')
      ).filter((c: string) => c)
    }

    // Extract experiences
    let experiences: Array<{ poste?: string; entreprise?: string; date_debut?: string; date_fin?: string; missions?: string[] } | string> = []
    if (sourceCandidate.work_history && Array.isArray(sourceCandidate.work_history) && sourceCandidate.work_history.length > 0) {
      experiences = sourceCandidate.work_history
    } else if (structuredProfile?.experience && Array.isArray(structuredProfile.experience)) {
      experiences = structuredProfile.experience
    } else if (latestProcessedUpload?.processed_data?.experience && Array.isArray(latestProcessedUpload.processed_data.experience)) {
      experiences = latestProcessedUpload.processed_data.experience
    } else if (latestProcessedUpload?.processed_data?.structured_profile?.experience && Array.isArray(latestProcessedUpload.processed_data.structured_profile.experience)) {
      experiences = latestProcessedUpload.processed_data.structured_profile.experience
    }

    // Extract education
    let education: string[] = []
    if (sourceCandidate.education && Array.isArray(sourceCandidate.education) && sourceCandidate.education.length > 0) {
      education = extractEducationList(sourceCandidate.education)
    } else if (structuredProfile?.formations && Array.isArray(structuredProfile.formations)) {
      education = extractEducationList(structuredProfile.formations)
    } else if (latestProcessedUpload?.processed_data?.education && Array.isArray(latestProcessedUpload.processed_data.education)) {
      education = extractEducationList(latestProcessedUpload.processed_data.education)
    } else if (latestProcessedUpload?.processed_data?.structured_profile?.formations && Array.isArray(latestProcessedUpload.processed_data.structured_profile.formations)) {
      education = extractEducationList(latestProcessedUpload.processed_data.structured_profile.formations)
    }

    // Extract career gaps and analysis
    let careerGaps: any[] = []
    let careerAnalysis: string[] = []
    let careerIntervals: any[] = []

    if ((sourceCandidate as any).career_gaps) {
      careerGaps = (sourceCandidate as any).career_gaps
    } else if (latestProcessedUpload?.processed_data?.career_gaps) {
      careerGaps = latestProcessedUpload.processed_data.career_gaps
    }

    if ((sourceCandidate as any).career_analysis_sentences) {
      careerAnalysis = (sourceCandidate as any).career_analysis_sentences
    } else if (latestProcessedUpload?.processed_data?.career_analysis_sentences) {
      careerAnalysis = latestProcessedUpload.processed_data.career_analysis_sentences
    }

    if ((sourceCandidate as any).career_intervals) {
      careerIntervals = (sourceCandidate as any).career_intervals
    } else if (latestProcessedUpload?.processed_data?.career_intervals) {
      careerIntervals = latestProcessedUpload.processed_data.career_intervals
    }

    return { projets, certifications, experiences, education, careerGaps, careerAnalysis, careerIntervals }
  }

  const [careerAnalysisExpanded, setCareerAnalysisExpanded] = useState(true)
  const [primaryColor, setPrimaryColor] = useState('#3B82F6') // Default blue-500

  // Get computed primary color from CSS variable
  useEffect(() => {
    const updatePrimaryColor = () => {
      // Create a temporary element to get computed color
      const tempEl = document.createElement('div')
      tempEl.style.color = 'hsl(var(--primary))'
      tempEl.style.position = 'absolute'
      tempEl.style.visibility = 'hidden'
      document.body.appendChild(tempEl)
      
      const computedColor = window.getComputedStyle(tempEl).color
      document.body.removeChild(tempEl)
      
      // Convert rgb/rgba to hex
      const rgbMatch = computedColor.match(/\d+/g)
      if (rgbMatch && rgbMatch.length >= 3) {
        const r = parseInt(rgbMatch[0])
        const g = parseInt(rgbMatch[1])
        const b = parseInt(rgbMatch[2])
        const toHex = (n: number) => {
          const hex = n.toString(16)
          return hex.length === 1 ? '0' + hex : hex
        }
        setPrimaryColor(`#${toHex(r)}${toHex(g)}${toHex(b)}`)
      }
    }
    
    updatePrimaryColor()
    // Update on theme change
    const observer = new MutationObserver(updatePrimaryColor)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    
    return () => observer.disconnect()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  const originPath = (location.state as { from?: string } | undefined)?.from
  const handleBackNavigation = () => {
    if (originPath) {
      navigate(originPath)
    } else {
      navigate('/candidates')
    }
  }

  if (!candidate) {
    return (
      <div className="text-center py-12 max-[1149px]:py-8 max-[1149px]:px-4">
        <h2 className="text-2xl font-bold text-foreground max-[1149px]:text-xl">{t('candidateDetail.candidateNotFound')}</h2>
        <Button onClick={handleBackNavigation} className="mt-4 max-[1149px]:h-9 max-[1149px]:text-sm">
          <ArrowLeft className="h-4 w-4 mr-2 max-[1149px]:mr-0" />
          <span className="max-[1149px]:text-sm">{t('candidateDetail.backToCandidates')}</span>
        </Button>
      </div>
    )
  }

  // Get the currently displayed candidate based on selectedProfileId (MUST be before getExtractedData)
  const displayedCandidate = selectedProfileId !== null && allProfilesWithEmail.length > 0
    ? allProfilesWithEmail.find(p => p.id === selectedProfileId) || candidate
    : candidate

  // Extract data from displayed candidate
  const { projets, certifications, experiences, education, careerGaps, careerAnalysis, careerIntervals } = getExtractedData()

  // Prepare data for the timeline graph
  const prepareTimelineData = () => {
    if (!careerIntervals || careerIntervals.length === 0) return []

    const events: { date: number; status: number; label: string; dateStr: string }[] = []

    // Sort intervals by date
    const sortedIntervals = [...careerIntervals].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())

    sortedIntervals.forEach((interval, index) => {
      const start = new Date(interval.start).getTime()
      const end = new Date(interval.end).getTime()

      // Point at start of work
      events.push({ date: start, status: 1, label: 'En poste', dateStr: interval.start })
      // Point at end of work
      events.push({ date: end, status: 1, label: 'En poste', dateStr: interval.end })

      // If there is a next interval, add a gap point if the gap is significant (> 1 day)
      if (index < sortedIntervals.length - 1) {
        const nextStart = new Date(sortedIntervals[index + 1].start).getTime()
        if (nextStart > end + 86400000) { // > 1 day
          // Drop to 0 immediately after end
          events.push({ date: end + 1, status: 0, label: 'Pause', dateStr: interval.end })
          // Stay at 0 until next start
          events.push({ date: nextStart - 1, status: 0, label: 'Pause', dateStr: sortedIntervals[index + 1].start })
        }
      }
    })

    // Check for gap after the last experience until today
    if (sortedIntervals.length > 0) {
      const lastInterval = sortedIntervals[sortedIntervals.length - 1]
      const lastEnd = new Date(lastInterval.end).getTime()
      const now = new Date().getTime()

      // If the last experience ended more than 30 days ago
      if (now > lastEnd + 30 * 24 * 60 * 60 * 1000) {
        // Drop to 0 immediately after end
        events.push({ date: lastEnd + 1, status: 0, label: 'Pause', dateStr: lastInterval.end })
        // Stay at 0 until now
        events.push({ date: now, status: 0, label: 'Pause', dateStr: new Date().toISOString() })
      }
    }

    return events
  }

  const timelineData = prepareTimelineData()

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="bg-card p-3 border border-border rounded-lg shadow-lg">
          <p className="text-sm font-semibold mb-1 text-foreground">{formatDate(new Date(data.date))}</p>
          <div className="flex items-center gap-2">
            <div 
              className="w-2 h-2 rounded-full" 
              style={{ backgroundColor: data.status === 1 ? primaryColor : '#eab308' }}
            />
            <span className="text-sm text-foreground">
              {data.status === 1 ? 'En poste' : 'Période de pause'}
            </span>
          </div>
        </div>
      )
    }
    return null
  }

  return (
    <div className="space-y-6 max-[1149px]:px-4 max-[1149px]:py-4 max-[1149px]:space-y-4">
      <div className="flex items-start justify-between max-[1149px]:gap-3 max-[640px]:flex-col max-[640px]:items-stretch max-[640px]:gap-4">
        <div className="flex items-center space-x-4 max-[1149px]:space-x-3 flex-1 min-w-0 max-[640px]:w-full">
          <Button variant="outline" onClick={handleBackNavigation} className="max-[1149px]:h-9 flex-shrink-0 max-[640px]:h-8">
            <ArrowLeft className="h-4 w-4 mr-2 max-[640px]:mr-0" />
            <span className="max-[640px]:hidden">{t('candidateDetail.back')}</span>
          </Button>
          <div className="flex-1 min-w-0">
            <PageHeader
              title={displayedCandidate.name}
              titleClassName="max-[1149px]:truncate max-[640px]:text-[32px]"
              description={translatedRole || displayedCandidate.role}
            />
            {displayedCandidate.source && (
              <Badge variant="secondary" className="mt-2 max-[1149px]:mt-1 max-[1149px]:text-xs max-[640px]:text-[10px] max-[640px]:mt-0.5">
                {t('candidateDetail.source')}:{' '}
                <span className="font-medium ml-1">
                  {t(`candidates.sources.${displayedCandidate.source}`, { defaultValue: displayedCandidate.source.replace(/_/g, ' ') })}
                </span>
              </Badge>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center max-[640px]:w-full max-[640px]:mt-0">
          {/* Quick Actions Dropdown */}
          <Dropdown
            className="max-[640px]:w-full"
            trigger={
              <Button
                variant="default"
                className="flex items-center justify-center space-x-2 max-[1149px]:h-9 max-[1149px]:text-sm max-[640px]:w-full max-[640px]:h-9"
              >
                <MoreVertical className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5 max-[640px]:h-3 max-[640px]:w-3" />
                <span className="max-[1149px]:text-sm max-[640px]:text-xs">{t('candidateDetail.quickActions')}</span>
                <ChevronDown className="h-4 w-4 transition-transform duration-200 max-[1149px]:h-3.5 max-[1149px]:w-3.5 max-[640px]:h-3 max-[640px]:w-3" />
              </Button>
            }
          >
            <>
              <DropdownItem
                onClick={() => setShowSendEmailModal(true)}
                className="hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-800 dark:hover:text-blue-300 transition-all duration-150 group"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-1.5 rounded-lg bg-blue-200 dark:bg-blue-900/50 group-hover:bg-blue-300 dark:group-hover:bg-blue-800/50 transition-colors duration-150">
                    <Mail className="h-4 w-4 text-blue-700 dark:text-blue-400" />
                  </div>
                  <div>
                    <div className="font-semibold">{t('candidateDetail.sendEmail')}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">{t('candidateDetail.sendEmailDescription')}</div>
                  </div>
                </div>
              </DropdownItem>
              <DropdownItem
                onClick={() => setScheduleMeetingType('call')}
                className="hover:bg-green-100 dark:hover:bg-green-900/30 hover:text-green-800 dark:hover:text-green-300 transition-all duration-150 group"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-1.5 rounded-lg bg-green-200 dark:bg-green-900/50 group-hover:bg-green-300 dark:group-hover:bg-green-800/50 transition-colors duration-150">
                    <Phone className="h-4 w-4 text-green-700 dark:text-green-400" />
                  </div>
                  <div>
                    <div className="font-semibold">{t('candidateDetail.scheduleCall')}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {t('candidateDetail.scheduleMeetingModal.callSubtitle', 'Prepare a phone catch-up')}
                    </div>
                  </div>
                </div>
              </DropdownItem>
              <DropdownItem
                onClick={() => setScheduleMeetingType('interview')}
                className="hover:bg-purple-100 dark:hover:bg-purple-900/30 hover:text-purple-800 dark:hover:text-purple-300 transition-all duration-150 group"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-1.5 rounded-lg bg-purple-200 dark:bg-purple-900/50 group-hover:bg-purple-300 dark:group-hover:bg-purple-800/50 transition-colors duration-150">
                    <Users className="h-4 w-4 text-purple-700 dark:text-purple-400" />
                  </div>
                  <div>
                    <div className="font-semibold">{t('candidateDetail.scheduleInterview')}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {t('candidateDetail.scheduleMeetingModal.interviewSubtitle', 'Plan an interview slot')}
                    </div>
                  </div>
                </div>
              </DropdownItem>
            </>
          </Dropdown>
        </div>
      </div>

      {/* Duplicate profiles list - sticky */}
      {allProfilesWithEmail.length > 1 && (
        <div className="sticky top-0 z-10 pb-4 -mb-2">
          <div className="flex flex-wrap gap-2">
            {allProfilesWithEmail.map((profile) => (
              <button
                key={profile.id}
                onClick={() => {
                  setSelectedProfileId(profile.id)
                  navigate(`/candidates/${profile.id}`, { replace: true })
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                  selectedProfileId === profile.id
                    ? 'bg-primary text-primary-foreground border-primary shadow-md'
                    : 'bg-background border-border hover:bg-muted hover:border-primary/50'
                }`}
              >
                <img
                  src={profile.profile_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.name)}&background=random`}
                  alt={profile.name}
                  className="h-8 w-8 rounded-full object-cover"
                />
                <div className="text-left">
                  <div className={`text-sm font-medium truncate ${selectedProfileId === profile.id ? 'text-primary-foreground' : 'text-foreground'}`}>
                    {profile.name}
                  </div>
                  <div className={`text-xs ${selectedProfileId === profile.id ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                    {profile.created_at && formatDate(new Date(profile.created_at))}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3 max-[1149px]:grid-cols-1 max-[1149px]:gap-4 items-stretch">
        {/* Main Content */}
        <div className="lg:col-span-2 flex flex-col gap-6 max-[1149px]:gap-4 min-h-0">
          <div className="space-y-6 max-[1149px]:space-y-4">
          {/* Candidate Info */}
          <Card>
            <CardHeader className="max-[1149px]:p-4">
              <div className="flex items-center justify-between max-[1149px]:flex-col max-[1149px]:items-stretch max-[1149px]:gap-3">
                <CardTitle className="text-base max-[1149px]:text-sm">{t('candidateDetail.candidateInformation')}</CardTitle>
                <Button variant="outline" onClick={() => setShowEditModal(true)} className="max-[1149px]:w-full max-[1149px]:h-9 max-[1149px]:text-sm">
                  <Edit className="h-4 w-4 mr-2 max-[1149px]:mr-0" />
                  <span className="max-[1149px]:text-sm">{t('candidateDetail.edit')}</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="max-[1149px]:p-4">
              <div className="flex items-start space-x-6 max-[1149px]:flex-col max-[1149px]:space-x-0 max-[1149px]:space-y-4">
                <img
                  src={displayedCandidate.profile_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayedCandidate.name)}&background=random`}
                  alt={displayedCandidate.name}
                  className="h-24 w-24 rounded-full object-cover flex-shrink-0 max-[1149px]:h-20 max-[1149px]:w-20 max-[1149px]:mx-auto"
                />
                <div className="flex-1 w-full min-w-0">
                  <div className="grid gap-4 md:grid-cols-2 max-[1149px]:grid-cols-1 max-[1149px]:gap-3">
                    <div className="space-y-3 max-[1149px]:space-y-2">
                      {(displayedCandidate as any).email && (
                        <div className="flex items-center space-x-2">
                          <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                          <span className="text-sm max-[1149px]:text-xs truncate">{displayedCandidate.email}</span>
                        </div>
                      )}
                      {(displayedCandidate as any).phone && (
                        <div className="flex items-center space-x-2">
                          <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                          <span className="text-sm max-[1149px]:text-xs truncate">{displayedCandidate.phone}</span>
                        </div>
                      )}
                      {(displayedCandidate as any).location && (
                        <div className="flex items-center space-x-2">
                          <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                          <span className="text-sm max-[1149px]:text-xs truncate">{displayedCandidate.location}</span>
                        </div>
                      )}
                      {(displayedCandidate as any).age && (
                        <div className="flex items-center space-x-2">
                          <Cake className="h-4 w-4 text-muted-foreground flex-shrink-0 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                          <span className="text-sm max-[1149px]:text-xs">{(displayedCandidate as any).age}</span>
                        </div>
                      )}
                      {(displayedCandidate as any).date_naissance && (
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                          <span className="text-sm max-[1149px]:text-xs">{(displayedCandidate as any).date_naissance}</span>
                        </div>
                      )}
                      {(displayedCandidate as any).genre && (
                        <div className="flex items-center space-x-2">
                          <Users className="h-4 w-4 text-muted-foreground flex-shrink-0 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                          <span className="text-sm max-[1149px]:text-xs">
                            {
                              (() => {
                                const g = (displayedCandidate as any).genre
                                if (!g) return ''
                                const val = String(g).trim()
                                if (val.toUpperCase() === 'H') return t('candidateDetail.gender_male')
                                if (val.toUpperCase() === 'F') return t('candidateDetail.gender_female')
                                return val
                              })()
                            }
                          </span>
                        </div>
                      )}
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                        <span className="text-sm max-[1149px]:text-xs">{t('candidateDetail.applied')} {formatDate(candidate.applied_date)}</span>
                      </div>
                    </div>
                    <div className="space-y-3 max-[1149px]:space-y-2">
                      <div>
                        <span className="text-sm font-medium max-[1149px]:text-xs">{t('candidateDetail.experience')}:</span>
                        <span className="text-sm text-muted-foreground ml-2 max-[1149px]:text-xs">{translateExperienceMessage(displayedCandidate.experience)}</span>
                      </div>
                      <div>
                        <span className="text-sm font-medium max-[1149px]:text-xs">{t('candidateDetail.lastContact')}:</span>
                        <span className="text-sm text-muted-foreground ml-2 max-[1149px]:text-xs">{formatDate(displayedCandidate.last_contact)}</span>
                      </div>
                      {/* Social Media Links */}
                      {(displayedCandidate.linkedin_url || displayedCandidate.github_url || displayedCandidate.twitter_url || displayedCandidate.portfolio_url) && (
                        <div className="flex items-center space-x-3 mt-3 max-[1149px]:mt-2 max-[1149px]:space-x-2 max-[1149px]:justify-center">
                          {displayedCandidate.linkedin_url && (
                            <a
                              href={displayedCandidate.linkedin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors flex-shrink-0"
                              title={t('candidateDetail.linkedIn', 'LinkedIn')}
                            >
                              <svg className="h-5 w-5 max-[1149px]:h-4 max-[1149px]:w-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                              </svg>
                            </a>
                          )}
                          {displayedCandidate.github_url && (
                            <a
                              href={displayedCandidate.github_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-800 dark:text-gray-200 hover:text-gray-600 dark:hover:text-gray-400 transition-colors flex-shrink-0"
                              title={t('candidateDetail.github', 'GitHub')}
                            >
                              <svg className="h-5 w-5 max-[1149px]:h-4 max-[1149px]:w-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                              </svg>
                            </a>
                          )}
                          {displayedCandidate.twitter_url && (
                            <a
                              href={displayedCandidate.twitter_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-500 transition-colors flex-shrink-0"
                              title={t('candidateDetail.twitter', 'Twitter')}
                            >
                              <svg className="h-5 w-5 max-[1149px]:h-4 max-[1149px]:w-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                              </svg>
                            </a>
                          )}
                          {displayedCandidate.portfolio_url && (
                            <a
                              href={displayedCandidate.portfolio_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 transition-colors flex-shrink-0"
                              title={t('candidateDetail.portfolio', 'Portfolio')}
                            >
                              <svg className="h-5 w-5 max-[1149px]:h-4 max-[1149px]:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                              </svg>
                            </a>
                          )}
                          {(candidate as any).others_url && (Array.isArray((candidate as any).others_url) ? (
                            (candidate as any).others_url.map((url: string, i: number) => (
                              <a
                                key={i}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors flex-shrink-0"
                                title={t('candidateDetail.otherUrl', 'Other URL')}
                              >
                                <Globe className="h-5 w-5 max-[1149px]:h-4 max-[1149px]:w-4" />
                              </a>
                            ))
                          ) : (
                            <a
                              href={(candidate as any).others_url as string}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors flex-shrink-0"
                              title={t('candidateDetail.otherUrl', 'Other URL')}
                            >
                              <Globe className="h-5 w-5 max-[1149px]:h-4 max-[1149px]:w-4" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full mt-6 pt-6 border-t space-y-4 max-[1149px]:mt-4 max-[1149px]:pt-4 max-[1149px]:space-y-3">
                  <div>
                    <span className="text-sm font-medium max-[1149px]:text-xs">{t('candidateDetail.bio')}:</span>
                    {candidate.bio ? (
                      <p className="text-sm text-muted-foreground mt-1 max-[1149px]:text-xs">{translatedBio || candidate.bio}</p>
                    ) : (
                      <p className="text-sm text-muted-foreground/60 italic mt-1 max-[1149px]:text-xs">
                        {t('candidateDetail.noBioAvailable', 'No biography available')}
                      </p>
                    )}
                  </div>


                  <div>
                    <span className="text-sm font-medium max-[1149px]:text-xs">{t('candidateDetail.skills')}:</span>
                    <div className="flex flex-wrap gap-2 mt-2 max-[1149px]:gap-1.5 max-[1149px]:mt-1.5">
                      {(i18n.language === 'en' && translatedHardSkills.length > 0 ? translatedHardSkills : (displayedCandidate.skills || [])).map((skill, index) => (
                        <Badge key={index} variant="secondary" className="max-[1149px]:text-xs max-[1149px]:px-1.5 max-[1149px]:py-0.5">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {(displayedCandidate as any).soft_skills && Array.isArray((displayedCandidate as any).soft_skills) && (displayedCandidate as any).soft_skills.length > 0 && (
                    <div>
                      <span className="text-sm font-medium max-[1149px]:text-xs">{t('candidateDetail.softSkills', 'Soft Skills')}:</span>
                      <div className="flex flex-wrap gap-2 mt-2 max-[1149px]:gap-1.5 max-[1149px]:mt-1.5">
                        {(translatedSoftSkills.length > 0 ? translatedSoftSkills : ((displayedCandidate as any).soft_skills || [])).map((skill: string, index: number) => (
                          <Badge key={index} variant="secondary" className="max-[1149px]:text-xs max-[1149px]:px-1.5 max-[1149px]:py-0.5">
                            <Zap className="h-3 w-3 mr-1 max-[1149px]:h-2.5 max-[1149px]:w-2.5 max-[1149px]:mr-0.5" />
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            </CardContent>
          </Card>

          {/* Languages Section */}
          {(displayedCandidate as any).languages && Array.isArray((displayedCandidate as any).languages) && (displayedCandidate as any).languages.length > 0 && (
            <Card>
              <CardHeader className="max-[1149px]:p-4">
                <CardTitle className="flex items-center space-x-2 text-base max-[1149px]:text-sm">
                  <Globe className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                  <span>{t('candidateDetail.languages', 'Languages')}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="max-[1149px]:p-4">
                <div className="flex flex-wrap gap-2 max-[1149px]:gap-1.5">
                  {(translatedLanguages.length > 0 ? translatedLanguages : ((displayedCandidate as any).languages || [])).map((lang: any, index: number) => {
                    const name = typeof lang === 'string' ? lang : lang.name || ''
                    const level = typeof lang === 'object' ? lang.level : ''

                    return (
                      <Badge key={index} variant="secondary" className="text-sm max-[1149px]:text-xs max-[1149px]:px-1.5 max-[1149px]:py-0.5">
                        {name} {level && `(${level})`}
                      </Badge>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Projects Section */}
          {projets && projets.length > 0 && (
            <Card>
              <CardHeader
                className="max-[1149px]:p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                onClick={() => setProjectsExpanded(!projectsExpanded)}
              >
                <CardTitle className="flex items-center justify-between text-base max-[1149px]:text-sm">
                  <div className="flex items-center space-x-2">
                    <FolderOpen className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                    <span>{t('candidateDetail.projects', 'Projects')}</span>
                    <Badge variant="secondary" className="text-xs max-[1149px]:text-[10px]">
                      {projets.length}
                    </Badge>
                  </div>
                  {projectsExpanded ? (
                    <ChevronUp className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                  ) : (
                    <ChevronDown className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                  )}
                </CardTitle>
              </CardHeader>
              {projectsExpanded && (
                <CardContent className="max-[1149px]:p-4">
                  <div className="space-y-4 max-[1149px]:space-y-3">
                    {(translatedProjects.length > 0 ? translatedProjects : projets).map((projet, index) => {
                      const isObject = typeof projet === 'object' && projet !== null
                      const nom = isObject ? (projet as any).nom || (projet as any).name : projet
                      const description = isObject ? (projet as any).description : null
                      const technologies = isObject ? ((projet as any).technologies || []) : []

                      return (
                        <div key={index} className="p-4 border rounded-lg max-[1149px]:p-3">
                          <h4 className="font-semibold text-sm mb-2 max-[1149px]:text-xs">{nom || t('candidateDetail.project', 'Project')} {index + 1}</h4>
                          {description && (
                            <p className="text-sm text-muted-foreground mb-2 max-[1149px]:text-xs">{description}</p>
                          )}
                          {technologies.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2 max-[1149px]:gap-1">
                              {technologies.map((tech: string, techIndex: number) => (
                                <Badge key={techIndex} variant="outline" className="text-xs max-[1149px]:text-[10px] max-[1149px]:px-1.5 max-[1149px]:py-0.5">
                                  {tech}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Main Missions Section */}
          {(displayedCandidate as any).main_missions && (
            <Card>
              <CardHeader
                className="max-[1149px]:p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                onClick={() => setMainMissionsExpanded(!mainMissionsExpanded)}
              >
                <CardTitle className="flex items-center justify-between text-base max-[1149px]:text-sm">
                  <div className="flex items-center space-x-2">
                    <Briefcase className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                    <span>{t('candidateDetail.mainMissions', 'Main Missions')}</span>
                    <Badge variant="secondary" className="text-xs max-[1149px]:text-[10px]">
                      {Array.isArray((displayedCandidate as any).main_missions) ? (displayedCandidate as any).main_missions.length : 1}
                    </Badge>
                  </div>
                  {mainMissionsExpanded ? (
                    <ChevronUp className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                  ) : (
                    <ChevronDown className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                  )}
                </CardTitle>
              </CardHeader>
              {mainMissionsExpanded && (
                <CardContent className="max-[1149px]:p-4">
                  <div className="space-y-3 max-[1149px]:space-y-2">
                    {(translatedMainMissions.length > 0 ? translatedMainMissions : (Array.isArray((displayedCandidate as any).main_missions) ? (displayedCandidate as any).main_missions : [(displayedCandidate as any).main_missions])).filter((item: any) => item && item.toString().trim() !== '').map((mission: string, index: number) => (
                      <div
                        key={index}
                        className="p-3 border rounded-lg bg-muted/40 text-sm text-foreground max-[1149px]:text-xs max-[1149px]:p-2"
                      >
                        {mission}
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Certifications Section */}
          {certifications && certifications.length > 0 && (
            <Card>
              <CardHeader className="max-[1149px]:p-4">
                <CardTitle className="flex items-center space-x-2 text-base max-[1149px]:text-sm">
                  <Award className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                  <span>{t('candidateDetail.certifications', 'Certifications')}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="max-[1149px]:p-4">
                <div className="flex flex-wrap gap-2 max-[1149px]:gap-1.5">
                  {(translatedCertifications.length > 0 ? translatedCertifications : certifications).map((cert, index) => (
                    <Badge key={index} variant="secondary" className="text-sm max-[1149px]:text-xs max-[1149px]:px-1.5 max-[1149px]:py-0.5">
                      <Award className="h-3 w-3 mr-1 max-[1149px]:h-2.5 max-[1149px]:w-2.5 max-[1149px]:mr-0.5" />
                      {cert}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Education Section */}
          {education && education.length > 0 && (
            <Card>
              <CardHeader
                className="max-[1149px]:p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                onClick={() => setEducationExpanded(!educationExpanded)}
              >
                <CardTitle className="flex items-center justify-between text-base max-[1149px]:text-sm">
                  <div className="flex items-center space-x-2">
                    <GraduationCap className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                    <span>{t('candidateDetail.education', 'Education')}</span>
                    <Badge variant="secondary" className="text-xs max-[1149px]:text-[10px]">
                      {education.length}
                    </Badge>
                  </div>
                  {educationExpanded ? (
                    <ChevronUp className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                  ) : (
                    <ChevronDown className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                  )}
                </CardTitle>
              </CardHeader>
              {educationExpanded && (
                <CardContent className="max-[1149px]:p-4">
                  <div className="space-y-3 max-[1149px]:space-y-2">
                    {(translatedEducations.length > 0 ? translatedEducations : education).map((entry, index) => (
                      <div
                        key={`${entry}-${index}`}
                        className="p-3 border rounded-lg bg-muted/40 text-sm text-foreground max-[1149px]:text-xs max-[1149px]:p-2"
                      >
                        {entry}
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Experiences/Work History Section */}
          {experiences && experiences.length > 0 && (
            <Card>
              <CardHeader
                className="max-[1149px]:p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                onClick={() => setExperiencesExpanded(!experiencesExpanded)}
              >
                <CardTitle className="flex items-center justify-between text-base max-[1149px]:text-sm">
                  <div className="flex items-center space-x-2">
                    <Building2 className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                    <span>{t('candidateDetail.experiences', 'Work Experience')}</span>
                    <Badge variant="secondary" className="text-xs max-[1149px]:text-[10px]">
                      {experiences.length}
                    </Badge>
                  </div>
                  {experiencesExpanded ? (
                    <ChevronUp className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                  ) : (
                    <ChevronDown className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                  )}
                </CardTitle>
              </CardHeader>
              {experiencesExpanded && (
                <CardContent className="max-[1149px]:p-4">
                  <div className="space-y-4 max-[1149px]:space-y-3">
                    {(translatedExperiences.length > 0 ? translatedExperiences : experiences).map((exp, index) => {
                      const isObject = typeof exp === 'object' && exp !== null
                      const poste = isObject ? (exp as any).poste || (exp as any).position : null
                      const entreprise = isObject ? (exp as any).entreprise || (exp as any).company : null
                      const date_debut = isObject ? (exp as any).date_debut || (exp as any).start_date : null
                      const date_fin = isObject ? (exp as any).date_fin || (exp as any).end_date : null
                      const missions = isObject ? ((exp as any).missions || []) : []
                      const title = isObject ? (poste && entreprise ? `${poste} - ${entreprise}` : (poste || entreprise || exp)) : exp

                      return (
                        <div key={index} className="p-4 border rounded-lg max-[1149px]:p-3">
                          <div className="flex items-start justify-between mb-2 max-[1149px]:mb-1.5 max-[1149px]:flex-col max-[1149px]:gap-1">
                            <h4 className="font-semibold text-sm max-[1149px]:text-xs">{title}</h4>
                            {(date_debut || date_fin) && (
                              <span className="text-xs text-muted-foreground whitespace-nowrap max-[1149px]:text-[10px]">
                                {date_debut || '?'} - {date_fin || t('candidateDetail.present', 'Present')}
                              </span>
                            )}
                          </div>
                          {missions.length > 0 && (
                            <ul className="list-disc list-inside space-y-1 mt-2 max-[1149px]:space-y-0.5">
                              {missions.map((mission: string, missionIndex: number) => (
                                <li key={missionIndex} className="text-sm text-muted-foreground max-[1149px]:text-xs">
                                  {mission}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          </div>

          {/* Resume & Documents */}
          <Card className="flex flex-col min-h-0 mt-auto shrink-0">
            <CardHeader className="max-[1149px]:p-4 shrink-0">
              <CardTitle className="flex items-center space-x-2 text-base max-[1149px]:text-sm">
                <UploadIcon className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                <span>{t('candidateDetail.resumeDocuments')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="max-[1149px]:p-4 flex-1 min-h-0 flex flex-col">
              {(uploads || []).length > 0 ? (
                <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1 max-[1149px]:space-y-2">
                  {(uploads || []).map((upload) => (
                    <div key={upload.id} className="flex items-center justify-between p-3 border rounded-lg max-[1149px]:flex-col max-[1149px]:items-stretch max-[1149px]:p-2 max-[1149px]:gap-2">
                    <div className="flex items-center space-x-3 max-[1149px]:space-x-2 flex-1 min-w-0">
                      {upload.file_type && upload.file_type.toLowerCase() === 'pdf' ? (
                        <File className="h-5 w-5 text-red-600 flex-shrink-0 max-[1149px]:h-4 max-[1149px]:w-4" />
                      ) : (
                        <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0 max-[1149px]:h-4 max-[1149px]:w-4" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate max-[1149px]:text-xs">{upload.original_filename}</p>
                        <p className="text-xs text-muted-foreground max-[1149px]:text-[10px]">
                          {t('candidateDetail.uploaded')} {formatDate(upload.uploaded_at)} • {(upload.file_size / 1024).toFixed(1)} KB • {upload.file_type.toUpperCase()}
                        </p>
                        <div className="flex items-center space-x-2 mt-1 max-[1149px]:space-x-1">
                          <Badge variant={upload.status === 'processed' ? 'default' : 'secondary'} className="text-xs max-[1149px]:text-[10px] max-[1149px]:px-1.5 max-[1149px]:py-0.5">
                            {upload.status}
                          </Badge>
                          {upload.file_type && upload.file_type.toLowerCase() === 'pdf' && (
                            <Badge variant="outline" className="text-xs text-red-600 max-[1149px]:text-[10px] max-[1149px]:px-1.5 max-[1149px]:py-0.5">
                              {t('candidateDetail.pdf')}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 max-[1149px]:w-full max-[1149px]:justify-end max-[1149px]:gap-2">
                      {upload.file_type && upload.file_type.toLowerCase() === 'pdf' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewPDF(upload)}
                          className="max-[1149px]:h-8 max-[1149px]:text-xs max-[1149px]:px-3"
                        >
                          <Eye className="h-4 w-4 mr-2 max-[1149px]:h-3.5 max-[1149px]:w-3.5 max-[1149px]:mr-0" />
                          <span className="max-[1149px]:text-xs">{t('candidateDetail.view')}</span>
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadFile(upload)}
                        className="max-[1149px]:h-8 max-[1149px]:text-xs max-[1149px]:px-3"
                      >
                        <Download className="h-4 w-4 mr-2 max-[1149px]:h-3.5 max-[1149px]:w-3.5 max-[1149px]:mr-0" />
                        <span className="max-[1149px]:text-xs">{t('candidateDetail.download')}</span>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground max-[1149px]:py-6">
                <UploadIcon className="h-12 w-12 mx-auto mb-4 opacity-50 max-[1149px]:h-8 max-[1149px]:w-8 max-[1149px]:mb-3" />
                <p className="max-[1149px]:text-sm">{t('candidateDetail.noDocumentsUploaded')}</p>
                <Button variant="outline" className="mt-4 max-[1149px]:mt-3 max-[1149px]:h-9 max-[1149px]:text-sm">
                  <UploadIcon className="h-4 w-4 mr-2 max-[1149px]:mr-0" />
                  <span className="max-[1149px]:text-sm">{t('candidateDetail.uploadResume')}</span>
                </Button>
              </div>
            )}
            </CardContent>
          </Card>

        </div>

        <div className="flex flex-col gap-6 max-[1149px]:gap-4 min-h-0">

          {/* Career Analysis / Gaps Section */}
          {(careerAnalysis.length > 0 || careerGaps.length > 0) && (
            <Card className="overflow-hidden border-l-4 border-l-primary">
              <CardHeader
                className="max-[1149px]:p-4 cursor-pointer hover:bg-muted/50 transition-colors bg-muted/30"
                onClick={() => setCareerAnalysisExpanded(!careerAnalysisExpanded)}
              >
                <CardTitle className="flex items-center justify-between text-base max-[1149px]:text-sm text-foreground">
                  <div className="flex items-center space-x-2">
                    <Clock className="h-5 w-5 text-primary max-[1149px]:h-4 max-[1149px]:w-4" />
                    <span>{t('candidateDetail.careerAnalysis', 'Analyse du Parcours')}</span>
                    {careerGaps.length > 0 && (
                      <Badge variant="outline" className="border-yellow-200 text-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-800 text-xs max-[1149px]:text-[10px]">
                        {careerGaps.length} {careerGaps.length === 1 ? 'Pause' : 'Pauses'}
                      </Badge>
                    )}
                  </div>
                  {careerAnalysisExpanded ? (
                    <ChevronUp className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5 text-primary" />
                  ) : (
                    <ChevronDown className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5 text-primary" />
                  )}
                </CardTitle>
              </CardHeader>
              {careerAnalysisExpanded && (
                <CardContent className="max-[1149px]:p-4 pt-6 space-y-6">

                  {/* Timeline Graph */}
                  {timelineData.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold flex items-center gap-2 text-foreground">
                        <CalendarDays className="h-4 w-4" />
                        {t('candidateDetail.careerTimeline', 'Frise Chronologique')}
                      </h4>
                      <div className="h-48 w-full bg-card rounded-xl border border-border p-2">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={timelineData}>
                            <defs>
                              <linearGradient id="colorStatus" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={primaryColor} stopOpacity={0.8} />
                                <stop offset="95%" stopColor={primaryColor} stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                            <XAxis
                              dataKey="date"
                              type="number"
                              domain={['auto', 'auto']}
                              tickFormatter={(unixTime) => new Date(unixTime).getFullYear().toString()}
                              stroke="hsl(var(--muted-foreground))"
                              tick={{ fontSize: 12 }}
                            />
                            <YAxis hide domain={[0, 1]} />
                            <Tooltip content={<CustomTooltip />} />
                            <Area
                              type="stepAfter"
                              dataKey="status"
                              stroke={primaryColor}
                              fillOpacity={1}
                              fill="url(#colorStatus)"
                              animationDuration={1500}
                            />
                            {careerGaps.map((gap, i) => (
                              <ReferenceLine
                                key={i}
                                x={new Date(gap.gap_start).getTime()}
                                stroke="#eab308"
                                strokeDasharray="3 3"
                                label={{ position: 'top', value: 'Gap', fill: '#eab308', fontSize: 10 }}
                              />
                            ))}
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* Gaps Visualization */}
                  {careerGaps.length > 0 ? (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
                        <AlertCircle className="h-4 w-4" />
                        {t('candidateDetail.gapsDetected', 'Pauses Détectées')}
                      </h4>
                      <div className="grid gap-3 grid-cols-1">
                        {careerGaps.map((gap, idx) => (
                          <div
                            key={idx}
                            className="group relative overflow-hidden rounded-xl bg-yellow-50/50 dark:bg-yellow-900/10 border border-yellow-100 dark:border-yellow-900/30 p-4 transition-all hover:bg-yellow-100/50 hover:shadow-md"
                          >
                            <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                              <Clock className="h-16 w-16 text-yellow-600" />
                            </div>

                            <div className="relative z-10">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold uppercase tracking-wider text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/40 px-2 py-1 rounded-md">
                                  {gap.gap_months_approx} Mois
                                </span>
                                <span className="text-xs text-yellow-600/70 dark:text-yellow-400/70">
                                  {gap.gap_days} jours
                                </span>
                              </div>

                              <div className="space-y-1">
                                <div className="flex items-center text-sm font-medium text-foreground">
                                  <span className="w-16 text-xs text-muted-foreground uppercase">Début</span>
                                  {formatDate(gap.gap_start)}
                                </div>
                                <div className="flex items-center text-sm font-medium text-foreground">
                                  <span className="w-16 text-xs text-muted-foreground uppercase">Fin</span>
                                  {formatDate(gap.gap_end)}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-100 dark:border-green-900/30">
                      <CheckCircle2 className="h-4 w-4" />
                      <span>{t('candidateDetail.noGaps', 'Aucune pause significative détectée entre les expériences.')}</span>
                    </div>
                  )}

                </CardContent>
              )}
            </Card>
          )}
          {/* Administration Comment + Received Emails */}
          <div className="flex flex-col flex-1 min-h-0">
            <Card className="shrink-0">
            <CardHeader className="max-[1149px]:p-4">
              <div className="flex items-center justify-between max-[1149px]:flex-col max-[1149px]:items-stretch max-[1149px]:gap-3">
                <CardTitle className="flex items-center space-x-2 text-base max-[1149px]:text-sm">
                  <Edit className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                  <span>{t('candidateDetail.adminComment')}</span>
                </CardTitle>
                {!isEditingComment && adminComment && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditingComment(true)}
                    className="max-[1149px]:w-full max-[1149px]:h-9 max-[1149px]:text-sm"
                  >
                    <Edit className="h-4 w-4 mr-2 max-[1149px]:mr-0" />
                    <span className="max-[1149px]:text-sm">{t('candidateDetail.edit')}</span>
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="max-[1149px]:p-4">
              {isEditingComment ? (
                <div className="space-y-3 max-[1149px]:space-y-2">
                  <textarea
                    value={adminComment}
                    onChange={(e) => setAdminComment(e.target.value)}
                    placeholder={t('candidateDetail.adminCommentPlaceholder')}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent max-[1149px]:p-2 max-[1149px]:text-sm"
                    rows={4}
                  />
                  <div className="flex items-center space-x-2 max-[1149px]:w-full max-[1149px]:gap-2">
                    <Button
                      onClick={handleSaveComment}
                      disabled={savingComment}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white max-[1149px]:flex-1 max-[1149px]:h-9 max-[1149px]:text-sm"
                    >
                      {savingComment ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 max-[1149px]:h-3.5 max-[1149px]:w-3.5 max-[1149px]:mr-1"></div>
                          <span className="max-[1149px]:text-xs">{t('common.saving')}</span>
                        </>
                      ) : (
                        <span className="max-[1149px]:text-xs">{t('common.save')}</span>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleCancelEdit}
                      size="sm"
                      className="max-[1149px]:flex-1 max-[1149px]:h-9 max-[1149px]:text-sm"
                    >
                      <span className="max-[1149px]:text-xs">{t('common.cancel')}</span>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 max-h-[420px] overflow-y-auto custom-scrollbar pr-1 max-[1149px]:space-y-2">
                  <div>
                    <h4 className="text-sm font-medium mb-2 max-[1149px]:text-xs">{t('candidateDetail.currentProfile')}</h4>
                    {adminComment ? (
                      <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-l-4 border-blue-500 max-[1149px]:p-2">
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap max-[1149px]:text-xs">
                          {adminComment}
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400 italic max-[1149px]:text-xs">{t('candidateDetail.noAdminComment')}</p>
                    )}
                  </div>

                  {/* Show comments from other profiles */}
                  {allProfilesWithEmail.length > 1 && (
                    <div className="border-t pt-3 max-[1149px]:pt-2">
                      <h4 className="text-sm font-medium mb-2 max-[1149px]:text-xs">Commentaires des autres profils</h4>
                      <div className="space-y-2 max-[1149px]:space-y-1.5">
                        {allProfilesWithEmail
                          .filter(p => p.id !== selectedProfileId)
                          .map(profile => (
                            <div key={profile.id} className="text-xs max-[1149px]:text-[10px]">
                              <p className="font-medium text-gray-600 dark:text-gray-400 mb-1">{profile.name} ({formatDate(new Date(profile.created_at))})</p>
                              {profile.admin_comment ? (
                                <div className="p-2 bg-gray-50 dark:bg-gray-800/50 rounded border-l-4 border-gray-400 ml-2">
                                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                    {profile.admin_comment}
                                  </p>
                                </div>
                              ) : (
                                <p className="text-gray-400 dark:text-gray-500 italic ml-2">{t('candidateDetail.noAdminComment')}</p>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {!adminComment && (
                    <div className="text-center py-4 text-gray-500 dark:text-gray-400 max-[1149px]:py-3">
                      <Edit className="h-8 w-8 mx-auto mb-2 opacity-50 max-[1149px]:h-6 max-[1149px]:w-6 max-[1149px]:mb-1.5" />
                      <p className="text-sm max-[1149px]:text-xs">{t('candidateDetail.noAdminComment')}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditingComment(true)}
                        className="mt-2 max-[1149px]:mt-1.5 max-[1149px]:h-9 max-[1149px]:text-sm"
                      >
                        <Edit className="h-4 w-4 mr-2 max-[1149px]:mr-0" />
                        <span className="max-[1149px]:text-sm">{t('candidateDetail.addComment')}</span>
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {candidate && (
            <CandidateReceivedEmails
              candidateId={candidate.id}
              refreshKey={emailRefreshKey}
              className="flex-1 min-h-0"
            />
          )}
          </div>

        </div>
      </div>

      <div className="space-y-6 max-[1149px]:space-y-4">
        {/* Applications */}
        <Card>
          <CardHeader className="max-[1149px]:p-4">
            <CardTitle className="flex items-center space-x-2 text-base max-[1149px]:text-sm">
              <Briefcase className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
              <span>{t('candidateDetail.applications')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="max-[1149px]:p-4">
            {loadingApplications ? (
              <div className="text-center py-4 max-[1149px]:py-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto max-[1149px]:h-6 max-[1149px]:w-6"></div>
                <p className="text-sm text-muted-foreground mt-2 max-[1149px]:text-xs max-[1149px]:mt-1.5">{t('candidateDetail.loadingApplications')}</p>
              </div>
            ) : candidateApplications.length > 0 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 max-[1149px]:gap-3">
                  {candidateApplications.map((application) => (
                    <div key={application.id} className="p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors max-[1149px]:p-2 h-full flex flex-col">
                      <div className="flex items-center justify-between mb-2 max-[1149px]:mb-1.5 max-[1149px]:flex-col max-[1149px]:items-stretch max-[1149px]:gap-2">
                        <h4 className="text-sm font-medium truncate max-[1149px]:text-xs">{application.offer_title}</h4>
                        {application.match_score > 0 && (
                          <Badge variant="secondary" className="max-[1149px]:text-xs max-[1149px]:px-1.5 max-[1149px]:py-0.5 max-[1149px]:w-fit">{application.match_score.toFixed(1)}%</Badge>
                        )}
                      </div>

                      {application.current_step_name && (
                        <div className="mb-2 max-[1149px]:mb-1.5">
                          <div className="flex items-center space-x-2 text-xs text-muted-foreground max-[1149px]:text-[10px] max-[1149px]:flex-wrap max-[1149px]:gap-1">
                            <span>{t('candidateDetail.currentStep')}:</span>
                            <Badge
                              variant="outline"
                              style={{
                                borderColor: '#3B82F6',
                                color: '#3B82F6'
                              }}
                              className="max-[1149px]:text-[10px] max-[1149px]:px-1.5 max-[1149px]:py-0.5"
                            >
                              {application.current_step_name}
                            </Badge>
                            {application.current_step_status && (
                              <span className="text-xs max-[1149px]:text-[10px]">({application.current_step_status})</span>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-auto pt-2 max-[1149px]:flex-col max-[1149px]:items-stretch max-[1149px]:gap-2">
                        <Badge variant="outline" className="max-[1149px]:text-xs max-[1149px]:px-1.5 max-[1149px]:py-0.5 max-[1149px]:w-fit">{application.status}</Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/offers/${application.offer_id}`)}
                          className="max-[1149px]:w-full max-[1149px]:h-8 max-[1149px]:text-xs"
                        >
                          <span className="max-[1149px]:text-xs">{t('candidateDetail.viewOffer')}</span>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {applicationsTotalPages > 1 && (
                  <div className="flex items-center justify-between pt-3 border-t gap-3 flex-wrap">
                    <p className="text-xs text-muted-foreground">
                      {t('common.showing')} {(applicationsPage - 1) * applicationsPageSize + 1}
                      -{Math.min(applicationsPage * applicationsPageSize, applicationsTotalCount)}{' '}
                      {t('common.of')} {applicationsTotalCount}
                    </p>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setApplicationsPage(1)}
                        disabled={applicationsPage === 1 || loadingApplications}
                        title={t('common.firstPage', 'First page')}
                      >
                        <ChevronsLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setApplicationsPage((prev) => Math.max(1, prev - 1))}
                        disabled={applicationsPage === 1 || loadingApplications}
                        title={t('common.previous', 'Previous')}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-xs font-bold text-foreground px-2 min-w-[3rem] text-center">
                        {applicationsPage}/{applicationsTotalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setApplicationsPage((prev) => Math.min(applicationsTotalPages, prev + 1))}
                        disabled={applicationsPage === applicationsTotalPages || loadingApplications}
                        title={t('common.next', 'Next')}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setApplicationsPage(applicationsTotalPages)}
                        disabled={applicationsPage === applicationsTotalPages || loadingApplications}
                        title={t('common.lastPage', 'Last page')}
                      >
                        <ChevronsRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground max-[1149px]:py-6">
                <Briefcase className="h-12 w-12 mx-auto mb-4 opacity-50 max-[1149px]:h-8 max-[1149px]:w-8 max-[1149px]:mb-3" />
                <p className="font-medium mb-2 max-[1149px]:text-sm max-[1149px]:mb-1.5">{t('candidateDetail.noApplications')}</p>
                <p className="text-sm max-[1149px]:text-xs">{t('candidateDetail.noApplicationsDescription')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* PDF Viewer Modal */}
      {viewingPDF && (
        <PDFViewer
          fileUrl={viewingPDF.url}
          fileName={viewingPDF.fileName}
          onClose={handleClosePDF}
        />
      )}

      {/* Edit Candidate Modal */}
      <EditCandidateModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        candidate={candidate}
        onCandidateUpdated={(updatedCandidate) => {
          // The saved English translation of the old bio would otherwise keep overriding the edited one
          if (updatedCandidate.bio !== candidate?.bio) {
            setTranslatedBio('')
          }
          // PATCH response omits id/updated_at etc., so merge instead of replacing
          setCandidate((prev) => (prev ? { ...prev, ...updatedCandidate } : updatedCandidate))
          setShowEditModal(false)
        }}
      />

      {/* Send Email Modal */}
      {candidate && (
        <SendEmailModal
          isOpen={showSendEmailModal}
          onClose={() => setShowSendEmailModal(false)}
          candidateEmail={candidate.email}
          candidateName={candidate.name}
          candidateId={candidate.id}
          onEmailSent={() => {
            setEmailRefreshKey((key) => key + 1)
          }}
        />
      )}

      {/* Schedule Meeting */}
      {candidate && scheduleMeetingType && (
        <ScheduleMeetingModal
          isOpen={scheduleMeetingType !== null}
          onClose={() => setScheduleMeetingType(null)}
          candidate={candidate}
          defaultType={scheduleMeetingType}
        />
      )}
    </div>
  )
}
